import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, resource, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TableColumnDirective, TableComponent } from '@shared/table/table.component';
import { ArticleDataService } from '@article/service/article-data.service';
import { lastValueFrom } from 'rxjs';
import { DropdownComponent } from '@shared/dropdown/dropdown.component';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

@Component({
  selector: 'app-technical-info-properties',
  imports: [
    TranslateModule,
    TableComponent,
    TableColumnDirective,
    DropdownComponent,
  ],
  templateUrl: './technical-info-properties.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class TechnicalInfoPropertiesComponent {
  private readonly articleDataService: ArticleDataService = inject(ArticleDataService);
  private readonly localeResourceManager: LocaleResourceManager = inject(LocaleResourceManager);

  public finish = new EventEmitter<void>();

  private readonly _articleId = signal<number>(-1);
  public set articleId(value: number) { this._articleId.set(value); }
  public get articleId(): number { return this._articleId(); }

  private readonly _language = signal<string>(this.localeResourceManager.currentLanguage.langKey);
  public set language(value: string) { this._language.set(value); }
  public get language(): string { return this._language(); }

  private readonly articleData = resource({
    request: () => this._articleId(),
    loader: ({ request: articleId }) => {
      return lastValueFrom(this.articleDataService.loadTechnicalInfoByArticleIds([articleId], false))
    },
  });

  public languages = computed(() => {
    const technicalInfos = this.articleData.value();
    if (!technicalInfos) {
      return [];
    }
    const localeKeys = new Set<string>();
    for (const propertyInLocale of (technicalInfos.get(this.articleId)?.articleTechnicalInfoVOs || [])) {
      for (const localizedValue of propertyInLocale.displayName.localizedValues) {
        localeKeys.add(localizedValue.localeKey);
      }
      for (const propertyValue of propertyInLocale.values) {
        localeKeys.add(propertyValue.locale);
      }
    }
    return Array.from(localeKeys);
  });

  public technicalProperties = computed(() => {
    const language = this._language();
    const propertiesInLocales = this.articleData.value()?.get(this.articleId)?.articleTechnicalInfoVOs || [];
    const technicalInfoProperties: { propertyDefinitionName: string, name: string; value: string; unit: string, type: string }[] = [];
    for (const propertyInLocale of propertiesInLocales) {
      let propertyDefinitionName = propertyInLocale.name;
      let name = '';
      let value = '';
      let unit = '';
      let type = '';
      name = propertyInLocale.displayName.localizedValues.find(displayName => displayName.localeKey === language)?.stringValue || '';
      const propertyValue = propertyInLocale.values.find(propertyValue => propertyValue.locale === language);
      if (propertyValue) {
        type = propertyValue.type;
        switch (propertyValue.type) {
          case 'STRING':
            value = String(propertyValue.stringValue);
            break;
          case 'INT':
            value = String(propertyValue.intValue);
            break;
          case 'FLOAT':
            value = String(propertyValue.floatValue);
            break;
          case 'INTRANGE':
            value = `${propertyValue.intRange.left} - ${propertyValue.intRange.right}`;
            break;
          case 'FLOATRANGE':
            value = `${propertyValue.floatRange.left} - ${propertyValue.floatRange.right}`;
            break;
          default:
            break;
        }
        if (/*propertyValue instanceof LocalizedPropertyValueWithUnitVO ||*/ 'unit' in propertyValue) {
          unit = <string>propertyValue.unit || '';
        }
      }
      technicalInfoProperties.push({ propertyDefinitionName, name, value, unit, type });
    }
    return technicalInfoProperties;
  });


  public async close(): Promise<void> {
    this.finish.emit();
  }
}
